import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Loader2, ArrowRight, Sparkles, Zap, AlertCircle, Lock, Share2, Trophy } from "lucide-react";
import { api, track } from "@/lib/api";
import { ScoreBars } from "@/components/ScoreBars";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { firstName } from "@/lib/name";
import { HowItWorks, Testimonials } from "@/components/Trust";
import { CareerGift } from "@/components/CareerGift";
import { draftToken, readDraft, saveDraft, hasReveal } from "@/lib/assessment";
import { apiError } from "@/lib/api";

const medals = ["🥇", "🥈", "🥉"];

export default function Result() {
  const [dna, setDna] = useState(null);
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const name = dna?.first_name || firstName(user);
  const userId = user?.id;
  const authLoading = user === null;

  useEffect(() => {
    if (authLoading) return;
    const load = async () => {
      try {
        let data;
        if (userId) {
          if (hasReveal()) { await saveDraft(); await refreshUser(); }
          data = (await api.get("/dna")).data;
        } else if (draftToken()) {
          const draft = (await readDraft()).data;
          if (!draft.dna) { navigate("/assessment", { replace: true }); return; }
          sessionStorage.setItem("nxt_assessment_revealed", "1");
          data = { ...draft.dna, first_name: draft.first_name, salary_note: "Indicative salary ranges; preference-fit scores are not hiring probabilities." };
        } else { navigate("/onboarding", { replace: true }); return; }
        setDna(data); track("career_result_viewed");
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.3 }, colors: ["#4F46E5", "#F59E0B", "#10B981"] });
      } catch (e) { setError(apiError(e)); }
    };
    load();
  }, [navigate, userId, authLoading, refreshUser]);

  if (error) return <main className="mx-auto max-w-xl px-5 py-24" data-testid="result-error"><p>{error}</p><button data-testid="result-retry" onClick={() => window.location.reload()} className="mt-4 font-bold underline">Try again</button><button data-testid="result-restart" onClick={() => navigate("/onboarding")} className="ml-5 font-bold underline">Start a new assessment</button></main>;

  if (!dna) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  const arch = dna.archetype;
  const superDim = [...dna.result_dimensions].sort((a, b) => b.value - a.value)[0];
  const lowDim = [...dna.result_dimensions].sort((a, b) => a.value - b.value)[0];

  return (
    <div className="min-h-screen bg-background pb-28">
      <div className="mx-auto max-w-xl px-5 py-6">
        <Logo className="mb-6" />

        {/* Archetype hero */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className={`overflow-hidden rounded-[28px] bg-gradient-to-br ${arch.color} p-7 text-white shadow-2xl`}
          data-testid="archetype-card">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">{name ? `${name}'s Career DNA` : "Your Career DNA"}</div>
          <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight [overflow-wrap:anywhere]" data-testid="reveal-headline">{name ? <><span className="text-white/80">{name}, meet</span> {arch.name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}.</> : arch.name}</h1>
          <p className="mt-2 text-lg text-white/80">"{arch.tagline}"</p>
          {dna.narrative && <p className="mt-4 text-sm leading-relaxed text-white/70">{dna.narrative.replace(/\*/g, "")}</p>}
        </motion.div>

        <section className="mt-6 rounded-3xl border border-emerald-200 bg-white p-6" data-testid="top-match-featured">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">A direction worth exploring</p>
          <div className="mt-3 flex items-start justify-between gap-4"><h2 className="font-display text-lg font-bold" data-testid="top-match-name">{dna.top_match.name}</h2><span className="font-mono text-3xl font-bold text-emerald-700" data-testid="top-match-percent">{dna.top_match.compatibility}%</span></div>
          <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="why-top-match">{dna.why_top_match}</p>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="match-score-disclaimer">Preference-fit indicator, not a prediction of getting hired.</p>
        </section>
        {!user && <section className="mt-6 rounded-3xl bg-slate-900 p-6 text-white" data-testid="save-dna-section">
          <h2 className="font-display text-lg font-bold">You built this. Keep it.</h2><p className="mt-2 text-sm text-slate-300">Save your matches, your gift and a plan you can return to. Your private draft lasts 24 hours.</p>
          <button data-testid="save-dna-btn" onClick={() => navigate("/signup?save=dna")} className="mt-4 w-full rounded-full bg-white px-5 py-3 font-bold text-slate-900 transition-colors hover:bg-emerald-100">Save {name}'s Career DNA</button>
          <button data-testid="save-dna-login" onClick={() => navigate("/login?save=dna")} className="mt-3 text-sm text-slate-300 hover:text-white">Already have an account? Log in to save</button>
        </section>}
        <CareerGift gift={dna.gift} />
        {dna.values_profile && <section className="mt-6 rounded-3xl border border-border bg-card p-6" data-testid="values-profile"><h2 className="font-display text-lg font-bold">What matters to you</h2><p className="mt-3 text-sm"><strong className="capitalize">{dna.values_profile.motivation}</strong> is a central motivator in your answers.</p><p className="mt-2 text-sm text-muted-foreground">{dna.values_profile.work_style} {dna.values_profile.success}</p></section>}

        {/* Dimensions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-6 rounded-3xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-bold">{dna.thinking_profile ? "How your mind approached the puzzles" : "Your Career DNA signals"}</h2>
          <ScoreBars dimensions={dna.thinking_profile || dna.result_dimensions} delay={0.4} />
          {dna.puzzle_insights && <details className="mt-5 text-sm"><summary data-testid="puzzle-insights-toggle" className="cursor-pointer font-bold text-indigo-700">The strengths behind your five choices</summary><ul className="mt-3 space-y-3">{dna.puzzle_insights.map(p => <li key={p.question_id} data-testid={`puzzle-insight-${p.question_id}`}><strong>{p.label}:</strong> {p.insight}</li>)}</ul></details>}
        </motion.div>

        {/* Superpower + blind spot */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6" data-testid="superpower-card">
            <div className="flex items-center gap-2 text-emerald-700"><Zap className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">Your Superpower</span></div>
            <p className="mt-2 font-display text-lg font-bold text-emerald-900">{arch.superpower}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="rounded-3xl border border-amber-200 bg-amber-50 p-6" data-testid="blindspot-card">
            <div className="flex items-center gap-2 text-amber-700"><AlertCircle className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">Your Blind Spot</span></div>
            <p className="mt-2 font-display text-lg font-bold text-amber-900">{arch.blind_spot}</p>
          </motion.div>
        </div>

        {/* Top matches */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="mt-6 rounded-3xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Your top career matches</h2>
          <div className="space-y-3">
            {(expanded ? dna.matches : dna.matches.slice(0, 3)).map((m, i) => (
              <div key={m.key} data-testid={`match-${m.key}`}
                className="flex items-center gap-3 rounded-2xl border border-border p-3.5 transition-colors hover:bg-secondary/50">
                <span className="w-6 text-center font-display text-lg font-bold text-muted-foreground">
                  {i < 3 ? medals[i] : i + 1}
                </span>
                <div className="flex-1">
                  <div className="font-bold">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.salary_mid} · {m.category}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-extrabold text-indigo-600">{m.compatibility}%</div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">match</div>
                </div>
              </div>
            ))}
            {dna.matches.length > 3 && <button data-testid="toggle-all-matches" onClick={() => setExpanded(!expanded)} className="py-2 text-sm font-bold text-indigo-700 hover:underline">{expanded ? "Show top three" : `Explore all ${dna.matches.length} matches — free`}</button>}
            {dna.peer_stat?.peers >= 100 && <p className="text-xs text-muted-foreground" data-testid="peer-stat">Across {dna.peer_stat.peers} saved profiles in this assessment version, {dna.peer_stat.pct_at_or_above}% showed both Analytical and Creative signals at or above yours. This is not a population ranking.</p>}
            <div className="flex items-center justify-between"><p className="text-[11px] text-muted-foreground" data-testid="guidance-note">Guidance, not guarantees — your DNA is a starting point, not a verdict.</p><HowItWorks /></div>
            <Testimonials compact limit={1} />
            {dna.locked_matches > 0 && (
              <button onClick={() => navigate("/paywall")} data-testid="unlock-matches-btn"
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-4 text-sm font-bold text-indigo-700 transition-colors hover:bg-indigo-50">
                <Lock className="h-4 w-4" /> Unlock {dna.locked_matches} more matches with NxT Plus
              </button>
            )}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">{dna.salary_note}</p>
        </motion.div>

        {/* CTAs */}
        {user && <div className="mt-7 space-y-3">
          <button onClick={() => navigate("/app/home")} data-testid="result-enter-os" className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 py-4 font-bold text-white hover:bg-emerald-800">Try my one-minute first mission <ArrowRight className="h-4 w-4" /></button>
          <button onClick={() => navigate("/wrapped")} data-testid="view-wrapped-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-4 font-bold text-white transition-all hover:bg-indigo-600 active:scale-[0.99]">
            <Share2 className="h-5 w-5" /> {name ? `Save & share ${name}'s Career DNA` : "See my Career Wrapped"}
          </button>
          <button onClick={() => navigate("/roadmap")} data-testid="view-roadmap-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card py-4 font-bold transition-colors hover:bg-secondary">
            <Trophy className="h-5 w-5 text-indigo-600" /> Build my career roadmap <ArrowRight className="h-4 w-4" />
          </button>
        </div>}
      </div>
    </div>
  );
}
